import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TaskNode } from './nodes/TaskNode';
import { MilestoneNode } from './nodes/MilestoneNode';
import { StartEndNode } from './nodes/StartEndNode';

import { ProjectTask } from '../../types/project';

interface VisualJourneyCanvasProps {
  projectId: string;
  tasks: ProjectTask[];
  selectedTaskId: string | null;
  onTaskSelect: (taskId: string) => void;
  onTaskStatusChange: (taskId: string, status: string) => void;
  viewMode: 'vertical' | 'flowchart' | 'timeline' | 'kanban';
}

const nodeTypes = {
  task: TaskNode,
  milestone: MilestoneNode,
  startEnd: StartEndNode,
};

export const VisualJourneyCanvas: React.FC<VisualJourneyCanvasProps> = ({
  projectId,
  tasks,
  selectedTaskId,
  onTaskSelect,
  onTaskStatusChange,
  viewMode,
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Convert tasks to nodes based on view mode
  const generateNodesAndEdges = useCallback(() => {
    if (viewMode === 'vertical') {
      return generateVerticalLayout(tasks, selectedTaskId, onTaskSelect, onTaskStatusChange);
    } else if (viewMode === 'flowchart') {
      return generateFlowchartLayout(tasks, selectedTaskId, onTaskSelect, onTaskStatusChange);
    } else if (viewMode === 'timeline') {
      return generateTimelineLayout(tasks, selectedTaskId, onTaskSelect, onTaskStatusChange);
    } else {
      return generateKanbanLayout(tasks, selectedTaskId, onTaskSelect, onTaskStatusChange);
    }
  }, [tasks, selectedTaskId, viewMode, onTaskSelect, onTaskStatusChange]);

  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = generateNodesAndEdges();
    setNodes(newNodes);
    setEdges(newEdges);
  }, [generateNodesAndEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  return (
    <div className="h-full w-full">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background />
        <Controls />
        <MiniMap />
      </ReactFlow>
    </div>
  );
};

// Layout generation functions
function generateVerticalLayout(
  tasks: ProjectTask[],
  selectedTaskId: string | null,
  onTaskSelect: (taskId: string) => void,
  onTaskStatusChange: (taskId: string, status: string) => void
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Add start node
  nodes.push({
    id: 'start',
    type: 'startEnd',
    position: { x: 250, y: 0 },
    data: { label: 'START', status: 'completed' },
  });

  // Calculate positions for tasks
  const sortedTasks = [...tasks].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));
  let yPosition = 150;
  const ySpacing = 200;
  const xBase = 250;

  // Group parallel tasks
  const taskGroups: ProjectTask[][] = [];
  let currentGroup: ProjectTask[] = [];

  sortedTasks.forEach((task, index) => {
    const hasParallelDependencies = task.depends_on && task.depends_on.length > 0 &&
      sortedTasks.some(t =>
        t.id !== task.id &&
        t.depends_on &&
        JSON.stringify((t.depends_on || []).sort()) === JSON.stringify((task.depends_on || []).sort())
      );

    if (hasParallelDependencies) {
      currentGroup.push(task);
    } else {
      if (currentGroup.length > 0) {
        taskGroups.push(currentGroup);
        currentGroup = [];
      }
      taskGroups.push([task]);
    }
  });

  if (currentGroup.length > 0) {
    taskGroups.push(currentGroup);
  }

  // Create nodes for each task group
  taskGroups.forEach((group, groupIndex) => {
    if (group.length === 1) {
      const task = group[0];
      nodes.push({
        id: task.id,
        type: task.task_type === 'milestone' ? 'milestone' : 'task',
        position: { x: xBase, y: yPosition },
        data: {
          task,
          selected: selectedTaskId === task.id,
          onSelect: () => onTaskSelect(task.id),
          onStatusChange: (status: string) => onTaskStatusChange(task.id, status),
        },
      });
    } else {
      // Parallel tasks - position side by side
      const xSpacing = 300;
      const startX = xBase - (xSpacing * (group.length - 1)) / 2;

      group.forEach((task, index) => {
        nodes.push({
          id: task.id,
          type: task.task_type === 'milestone' ? 'milestone' : 'task',
          position: { x: startX + (index * xSpacing), y: yPosition },
          data: {
            task,
            selected: selectedTaskId === task.id,
            onSelect: () => onTaskSelect(task.id),
            onStatusChange: (status: string) => onTaskStatusChange(task.id, status),
          },
        });
      });
    }

    yPosition += ySpacing;
  });

  // Add end node
  nodes.push({
    id: 'end',
    type: 'startEnd',
    position: { x: xBase, y: yPosition },
    data: { label: 'COMPLETE', status: 'pending' },
  });

  // Create edges
  let previousNodeIds = ['start'];

  taskGroups.forEach((group) => {
    const groupNodeIds = group.map(task => task.id);

    // Connect from previous nodes to current group
    previousNodeIds.forEach(prevId => {
      groupNodeIds.forEach(currId => {
        const task = group.find(t => t.id === currId);
        if (!task?.depends_on || task.depends_on.length === 0 || task.depends_on.includes(prevId)) {
          edges.push({
            id: `${prevId}-${currId}`,
            source: prevId,
            target: currId,
            type: 'smoothstep',
            animated: nodes.find(n => n.id === prevId)?.data?.task?.status === 'in_progress',
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
            },
          });
        }
      });
    });

    previousNodeIds = groupNodeIds;
  });

  // Connect last tasks to end
  previousNodeIds.forEach(prevId => {
    edges.push({
      id: `${prevId}-end`,
      source: prevId,
      target: 'end',
      type: 'smoothstep',
    });
  });

  return { nodes, edges };
}

function generateFlowchartLayout(
  tasks: ProjectTask[],
  selectedTaskId: string | null,
  onTaskSelect: (taskId: string) => void,
  onTaskStatusChange: (taskId: string, status: string) => void
): { nodes: Node[]; edges: Edge[] } {
  // Simplified flowchart layout - can be enhanced later
  return generateVerticalLayout(tasks, selectedTaskId, onTaskSelect, onTaskStatusChange);
}

function generateTimelineLayout(
  tasks: ProjectTask[],
  selectedTaskId: string | null,
  onTaskSelect: (taskId: string) => void,
  onTaskStatusChange: (taskId: string, status: string) => void
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Sort tasks by due date
  const sortedTasks = [...tasks].sort((a, b) => {
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  // Position tasks along a horizontal timeline
  const xSpacing = 300;
  const yBase = 300;

  sortedTasks.forEach((task, index) => {
    nodes.push({
      id: task.id,
      type: task.task_type === 'milestone' ? 'milestone' : 'task',
      position: { x: 100 + (index * xSpacing), y: yBase },
      data: {
        task,
        selected: selectedTaskId === task.id,
        onSelect: () => onTaskSelect(task.id),
        onStatusChange: (status: string) => onTaskStatusChange(task.id, status),
      },
    });
  });

  // Create edges based on dependencies
  tasks.forEach(task => {
    if (task.depends_on) {
      task.depends_on.forEach(depId => {
        edges.push({
          id: `${depId}-${task.id}`,
          source: depId,
          target: task.id,
          type: 'smoothstep',
        });
      });
    }
  });

  return { nodes, edges };
}

function generateKanbanLayout(
  tasks: ProjectTask[],
  selectedTaskId: string | null,
  onTaskSelect: (taskId: string) => void,
  onTaskStatusChange: (taskId: string, status: string) => void
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = [];
  const edges: Edge[] = [];

  // Group tasks by status
  const columns = {
    pending: { x: 100, y: 100, tasks: [] as ProjectTask[] },
    in_progress: { x: 400, y: 100, tasks: [] as ProjectTask[] },
    blocked: { x: 700, y: 100, tasks: [] as ProjectTask[] },
    completed: { x: 1000, y: 100, tasks: [] as ProjectTask[] },
  };

  tasks.forEach(task => {
    columns[task.status].tasks.push(task);
  });

  // Create nodes for each column
  Object.entries(columns).forEach(([status, column]) => {
    // Add column header
    nodes.push({
      id: `column-${status}`,
      type: 'startEnd',
      position: { x: column.x, y: 0 },
      data: {
        label: status.replace(/_/g, ' ').toUpperCase(),
        status: 'default'
      },
      draggable: false,
    });

    // Add tasks in column
    column.tasks.forEach((task, index) => {
      nodes.push({
        id: task.id,
        type: task.task_type === 'milestone' ? 'milestone' : 'task',
        position: { x: column.x, y: column.y + (index * 150) },
        data: {
          task,
          selected: selectedTaskId === task.id,
          onSelect: () => onTaskSelect(task.id),
          onStatusChange: (status: string) => onTaskStatusChange(task.id, status),
        },
      });
    });
  });

  return { nodes, edges };
}